import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { I18nProvider } from "@/i18n";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import DisconnectedClients from "@/pages/DisconnectedClients";
import PendingActivationClients from "@/pages/PendingActivationClients";
import ReconnectionQueue from "@/pages/ReconnectionQueue";
import PlanesFinanciero from "@/pages/PlanesFinanciero";
import Invoices from "@/pages/Invoices";
import Accounting from "@/pages/Accounting";
import Reports from "@/pages/Reports";
import CalendarPage from "@/pages/CalendarPage";
import ActivityPage from "@/pages/ActivityPage";
import EmailLogPage from "@/pages/EmailLogPage";
import PayoutsPage from "@/pages/PayoutsPage";
import CommunicationsPage from "@/pages/CommunicationsPage";
import NewConnectionPage from "@/pages/NewConnectionPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import SecurityPage from "@/pages/SecurityPage";
import TrashPage from "@/pages/TrashPage";
import BackupsPage from "@/pages/BackupsPage";
import AgentsPage from "@/pages/AgentsPage";
import Layout from "@/components/Layout";
import SupportLayout from "@/components/SupportLayout";
import SupportDashboard from "@/pages/support/SupportDashboard";
import LeadsPage from "@/pages/support/LeadsPage";
import OrdersPage from "@/pages/support/OrdersPage";
import SupportClientsPage from "@/pages/support/SupportClientsPage";
import PlansPage from "@/pages/support/PlansPage";
import UsersPage from "@/pages/support/UsersPage";

function Loading() { return <div className="h-screen flex items-center justify-center text-slate-500">Cargando...</div>; }

function Forbidden({ redirectTo, label }) {
  return (
    <div className="h-screen flex items-center justify-center flex-col gap-4 px-6 text-center">
      <div className="text-5xl font-mono text-slate-300">403</div>
      <h1 className="text-xl font-semibold text-slate-900">Acceso restringido</h1>
      <p className="text-sm text-slate-600 max-w-md">Tu cuenta no tiene permisos para acceder a esta sección.</p>
      <a href={redirectTo} className="text-sm text-blue-600 hover:underline mt-2">Ir a {label} →</a>
    </div>
  );
}

const FINANCIAL_ROLES = ["admin", "limited_admin", "super_admin", "customer_service"];
const FINANCE_ONLY_ROLES = ["admin", "super_admin"]; // Contable + Super — see money endpoints
const SUPPORT_ROLES = ["agent", "support_admin", "super_admin"];
const SUPPORT_ADMIN_ROLES = ["support_admin", "super_admin"];

function FinancialRoute({ children, requireFullAdmin = false, requireFinance = false, requireService = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!FINANCIAL_ROLES.includes(user.role)) return <Forbidden redirectTo="/support" label="Support" />;
  // Full admin required (admin + super_admin) — no customer_service, no limited_admin
  if (requireFullAdmin && !FINANCE_ONLY_ROLES.includes(user.role)) {
    return <Layout><Forbidden redirectTo="/" label="Dashboard" /></Layout>;
  }
  // Finance-only routes: block customer_service
  if (requireFinance && user.role === "customer_service") {
    return <Layout><Forbidden redirectTo="/clientes" label="Clientes" /></Layout>;
  }
  // Service-only routes: block accountants (contable)
  if (requireService && user.role === "admin") {
    return <Layout><Forbidden redirectTo="/" label="Dashboard" /></Layout>;
  }
  return <Layout>{children}</Layout>;
}

function SupportRoute({ children, requireAdmin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/support/login" replace />;
  if (!SUPPORT_ROLES.includes(user.role)) return <Forbidden redirectTo="/" label="Dashboard Financiero" />;
  if (requireAdmin && !SUPPORT_ADMIN_ROLES.includes(user.role)) {
    return (
      <SupportLayout>
        <Forbidden redirectTo="/support" label="Support Dashboard" />
      </SupportLayout>
    );
  }
  return <SupportLayout>{children}</SupportLayout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) {
    // Priority: super_admin → admin panel; then role-specific defaults.
    if (user.role === "super_admin") return <Navigate to="/" replace />;
    if (user.role === "customer_service") return <Navigate to="/clientes" replace />;
    if (SUPPORT_ROLES.includes(user.role)) return <Navigate to="/support" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

// Redirects the root route: customer_service → /clientes (they don't need the finance dashboard).
function DashboardOrClientsByRole() {
  const { user } = useAuth();
  if (user?.role === "customer_service") return <Navigate to="/clientes" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              {/* Legacy support login → redirect to unified login */}
              <Route path="/support/login" element={<Navigate to="/login" replace />} />

              {/* Common — everyone in the admin dashboard sees these */}
              <Route path="/" element={
                <FinancialRoute>
                  <DashboardOrClientsByRole />
                </FinancialRoute>
              } />
              <Route path="/clientes" element={<FinancialRoute><Clients /></FinancialRoute>} />
              <Route path="/clientes/pendientes" element={<FinancialRoute><PendingActivationClients /></FinancialRoute>} />
              <Route path="/clientes/desconectados" element={<FinancialRoute><DisconnectedClients /></FinancialRoute>} />
              <Route path="/clientes/reconexion" element={<FinancialRoute><ReconnectionQueue /></FinancialRoute>} />
              <Route path="/actividad" element={<FinancialRoute><ActivityPage /></FinancialRoute>} />
              <Route path="/correos" element={<FinancialRoute><EmailLogPage /></FinancialRoute>} />
              <Route path="/comunicacion" element={<FinancialRoute><CommunicationsPage /></FinancialRoute>} />
              <Route path="/facturas" element={<FinancialRoute><Invoices /></FinancialRoute>} />
              <Route path="/papelera" element={<FinancialRoute><TrashPage /></FinancialRoute>} />

              {/* Service-facing routes — accountant blocked */}
              <Route path="/conexion-nueva" element={<FinancialRoute requireService><NewConnectionPage /></FinancialRoute>} />
              <Route path="/calendario" element={<FinancialRoute><CalendarPage /></FinancialRoute>} />

              {/* Finance-only routes — customer_service blocked */}
              <Route path="/planes" element={<FinancialRoute requireFinance><PlanesFinanciero /></FinancialRoute>} />
              <Route path="/contabilidad" element={<FinancialRoute requireFinance><Accounting /></FinancialRoute>} />
              <Route path="/reportes" element={<FinancialRoute requireFinance><Reports /></FinancialRoute>} />
              <Route path="/payouts" element={<FinancialRoute requireFinance><PayoutsPage /></FinancialRoute>} />
              <Route path="/agentes" element={<FinancialRoute requireFinance><AgentsPage /></FinancialRoute>} />

              {/* Super/full-admin only */}
              <Route path="/admins" element={<FinancialRoute requireFullAdmin><AdminUsersPage /></FinancialRoute>} />
              <Route path="/seguridad" element={<FinancialRoute><SecurityPage /></FinancialRoute>} />
              <Route path="/respaldos" element={<FinancialRoute requireFullAdmin><BackupsPage /></FinancialRoute>} />

              {/* Support dashboard */}
              <Route path="/support" element={<SupportRoute><SupportDashboard /></SupportRoute>} />
              <Route path="/support/orders" element={<SupportRoute><OrdersPage /></SupportRoute>} />
              <Route path="/support/leads" element={<SupportRoute><LeadsPage /></SupportRoute>} />
              <Route path="/support/leads/:filter" element={<SupportRoute><LeadsPage /></SupportRoute>} />
              <Route path="/support/clients" element={<Navigate to="/support/clients/active" replace />} />
              <Route path="/support/clients/:filter" element={<SupportRoute><SupportClientsPage /></SupportRoute>} />
              <Route path="/support/plans" element={<SupportRoute><PlansPage apiBase="/support/plans" readOnly={true} /></SupportRoute>} />
              <Route path="/support/users" element={<SupportRoute><UsersPage /></SupportRoute>} />
              <Route path="/support/activity" element={<SupportRoute><ActivityPage /></SupportRoute>} />
              <Route path="/support/communications" element={<SupportRoute><CommunicationsPage /></SupportRoute>} />
              <Route path="/support/security" element={<SupportRoute><SecurityPage /></SupportRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
